import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock3, Smartphone, CreditCard, Landmark, RotateCcw, History } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { api, errMsg, inr, dt } from "../../lib/api";
import { Card, Notice, PageTitle, StatusBadge, Table } from "../../components/ui-kit";
import { PlanCard } from "../../components/PlanCard";

const METHODS = { upi: [Smartphone, "UPI"], card: [CreditCard, "Card"], netbanking: [Landmark, "Net banking"] };

export function Plans() {
  const { t } = useLang();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [mode, setMode] = useState(null);
  useEffect(() => {
    api.get("/partner/dashboard").then((r) => setD(r.data));
    api.get("/payments/gateway").then((r) => setMode(r.data.mode)).catch((e) => console.error("Gateway status failed:", e));
  }, []);
  if (!d) return null;
  return (
    <div data-testid="partner-plans">
      <PageTitle title={t("Plans & upgrades", "प्लान और अपग्रेड")} sub={t("One-time payments. Pay only the difference when upgrading.", "एक बार का भुगतान। अपग्रेड पर केवल अंतर चुकाएँ।")}>
        <Link to="/app/payments" className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-sm font-semibold" data-testid="view-txn-history"><History size={16} />{t("Transaction history", "लेनदेन इतिहास")}</Link>
      </PageTitle>
      {mode === "test" && <div className="mb-6"><Notice tone="purple" testid="test-mode-notice">{t("TEST MODE — no real money is charged. Live payments stay disabled until the payment provider approves SurveyKaro.", "टेस्ट मोड — कोई असली पैसा नहीं कटेगा। प्रोवाइडर की स्वीकृति तक लाइव भुगतान बंद हैं।")}</Notice></div>}
      <div className="grid gap-6 md:grid-cols-3">
        {d.plans.map((p) => (
          <PlanCard key={p.id} plan={p} current={d.plan?.id === p.id} disabled={d.plan && p.price <= d.plan.price} upgradeFrom={d.plan} onPay={(pl) => nav(`/app/checkout/${pl.id}`)} />
        ))}
      </div>
    </div>
  );
}

function loadRazorpay() {
  return new Promise((res) => {
    if (window.Razorpay) return res(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => res(true);
    s.onerror = () => res(false);
    document.body.appendChild(s);
  });
}

export function Checkout() {
  const { planId } = useParams();
  const { t } = useLang();
  const nav = useNavigate();
  const [q, setQ] = useState(null);
  const [err, setErr] = useState(null);
  const [order, setOrder] = useState(null);
  const [method, setMethod] = useState("upi");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/payments/quote/${planId}`).then((r) => setQ(r.data)).catch((e) => setErr(errMsg(e))); }, [planId]);
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/orders", { plan_id: planId, idempotency_key: `${planId}-${Date.now()}` });
      setOrder(data);
      if (data.provider === "razorpay") {
        if (!(await loadRazorpay())) throw new Error("Could not load Razorpay");
        const rz = new window.Razorpay({
          key: data.razorpay_key_id, order_id: data.provider_order_id, amount: Math.round(data.total * 100), currency: "INR", name: "SurveyKaro",
          description: data.plan_name,
          handler: async (resp) => { try { await api.post("/payments/razorpay/verify", resp); } catch (e) { toast.error(errMsg(e)); } nav(`/app/payments/${data.id}`); },
          modal: { ondismiss: () => nav(`/app/payments/${data.id}`) },
        });
        rz.open();
      }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const simulate = async (outcome) => {
    setBusy(true);
    try { await api.post("/payments/sandbox/simulate", { order_id: order.id, outcome, method }); nav(`/app/payments/${order.id}`); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  if (err) return <Card><Notice tone="red" testid="checkout-error">{err}</Notice><Link to="/app/plans" className="mt-4 inline-block text-sm font-semibold text-blue-400">{t("Back to plans", "प्लान पर वापस")}</Link></Card>;
  if (!q) return <div className="h-40 animate-pulse rounded-2xl bg-white/10" />;
  const gw = q.gateway;
  const Row = ({ l, v, b, testid }) => <div className={`flex justify-between py-2 text-sm ${b ? "border-t border-white/10 pt-3 text-base font-bold text-white" : "text-slate-500"}`}><span>{l}</span><span data-testid={testid}>{v}</span></div>;
  return (
    <div className="mx-auto grid max-w-5xl gap-6 lg:grid-cols-[1.2fr_1fr]" data-testid="checkout-page">
      <Card>
        <p className="text-xs font-bold uppercase tracking-wider text-blue-400">{q.kind === "upgrade" ? t("Plan upgrade", "प्लान अपग्रेड") : t("Plan activation", "प्लान एक्टिवेशन")}</p>
        <h1 className="mt-1 text-2xl font-bold text-white" data-testid="checkout-plan-name">{t(q.plan_name_en, q.plan_name_hi)}</h1>
        {q.from_plan && <p className="mt-1 text-sm text-slate-500">{t("From", "से")}: {q.from_plan}</p>}
        <div className="mt-6">
          <Row l={t("Plan price", "प्लान मूल्य")} v={inr(q.plan_price)} />
          {q.kind === "upgrade" && <Row l={t("Less: current plan", "घटाएँ: वर्तमान प्लान")} v={`− ${inr(q.credit_from_current)}`} />}
          <Row l={q.inclusive ? t("Base amount", "मूल राशि") : t("Activation / upgrade fee", "शुल्क")} v={inr(q.base)} testid="checkout-base" />
          <Row l={`GST @ ${q.rate}% ${q.inclusive ? "(incl.)" : "(excl.)"}`} v={inr(q.tax)} testid="checkout-gst" />
          <Row b l={t("Total payable", "कुल देय")} v={inr(q.total)} testid="checkout-total" />
        </div>
        {q.tax_rule_status !== "approved" && <p className="mt-2 text-[11px] text-amber-700">{t("GST configuration is pending tax-professional review. A payment receipt (not a tax invoice) will be issued.", "GST कॉन्फ़िगरेशन टैक्स विशेषज्ञ समीक्षा हेतु लंबित है। रसीद मिलेगी (टैक्स इनवॉइस नहीं)।")}</p>}
        <div className="mt-6 space-y-2 text-sm text-slate-500">
          <p>• {t("One-time payment, not a subscription. No auto-renewal.", "एक बार का भुगतान, सब्सक्रिप्शन नहीं। कोई ऑटो-रिन्यूअल नहीं।")}</p>
          <p>• {t("Plan activates only after server-verified payment confirmation.", "सर्वर-सत्यापित भुगतान के बाद ही प्लान सक्रिय होगा।")}</p>
          <p>• {t("New daily cap after activation", "सक्रियता के बाद नई दैनिक सीमा")}: <b>{inr(q.daily_cap)}</b> ({t("maximum, not guaranteed", "अधिकतम, गारंटी नहीं")})</p>
          <p>• {t("Refunds", "रिफंड")}: <Link to="/policy/refund" target="_blank" className="font-semibold text-blue-400 underline" data-testid="checkout-refund-link">{t("Refund & Cancellation Policy", "रिफंड और रद्दीकरण नीति")}</Link></p>
        </div>
      </Card>
      <Card className="h-fit">
        <p className="font-bold text-white">{t("Payment method", "भुगतान माध्यम")}</p>
        {gw.provider === "unavailable" || !gw.enabled ? <div className="mt-3"><Notice tone="red" testid="gateway-unavailable">{t("Payments are currently unavailable.", "भुगतान अभी उपलब्ध नहीं है।")}</Notice></div> : (
          <>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {gw.methods.filter((m) => METHODS[m]).map((m) => { const [I, l] = METHODS[m]; return <button key={m} onClick={() => setMethod(m)} data-testid={`method-${m}`} className={`flex flex-col items-center gap-1 rounded-xl border p-3 text-xs font-semibold ${method === m ? "border-blue-500 bg-blue-500/100/10 text-blue-400" : "border-white/10 text-slate-500"}`}><I size={18} />{l}</button>; })}
            </div>
            <p className="mt-2 text-[11px] text-slate-500">{gw.provider === "sandbox" ? t("Sandbox simulator — methods are simulated.", "सैंडबॉक्स सिम्युलेटर — माध्यम सिम्युलेटेड हैं।") : t("Final methods are shown by Razorpay.", "अंतिम माध्यम Razorpay दिखाएगा।")}</p>
            {!order || order.provider !== "sandbox" ? (
              <button onClick={pay} disabled={busy} className="mt-5 w-full rounded-full bg-gradient-to-r from-blue-600 to-blue-500 py-3.5 text-sm font-bold text-white shadow-lg shadow-blue-600/20 hover:brightness-110 disabled:opacity-50" data-testid="checkout-pay-now-btn">
                Pay Now / अभी भुगतान करें • {inr(q.total)}
              </button>
            ) : (
              <div className="mt-5 space-y-2 rounded-2xl border-2 border-dashed border-purple-500/30 bg-purple-500/10 p-4" data-testid="sandbox-panel">
                <p className="text-xs font-bold uppercase tracking-wider text-purple-400">SANDBOX TEST GATEWAY — {t("not a real payment", "असली भुगतान नहीं")}</p>
                <p className="text-xs text-purple-900">Order {order.provider_order_id} • {inr(order.total)}{order.reused && ` • ${t("existing open order reused", "मौजूदा ऑर्डर उपयोग")}`}</p>
                <button disabled={busy} onClick={() => simulate("success")} className="w-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 py-2.5 text-sm font-bold text-white" data-testid="sandbox-success-btn">{t("Simulate successful payment", "सफल भुगतान सिम्युलेट करें")}</button>
                <div className="grid grid-cols-3 gap-2">
                  <button disabled={busy} onClick={() => simulate("pending")} className="rounded-full border border-amber-300 bg-white/5 py-2 text-xs font-semibold text-amber-700" data-testid="sandbox-pending-btn">Pending</button>
                  <button disabled={busy} onClick={() => simulate("failed")} className="rounded-full border border-red-500/30 bg-white/5 py-2 text-xs font-semibold text-red-700" data-testid="sandbox-fail-btn">Fail</button>
                  <button disabled={busy} onClick={() => simulate("cancelled")} className="rounded-full glass py-2 text-xs font-semibold text-slate-300" data-testid="sandbox-cancel-btn">Cancel</button>
                </div>
              </div>
            )}
          </>
        )}
        <p className="mt-4 text-center text-[11px] text-slate-500">Mode: {String(gw.mode).toUpperCase()} • Provider: {gw.provider}</p>
      </Card>
    </div>
  );
}

export function Receipt() {
  const { id } = useParams();
  const { t } = useLang();
  const [o, setO] = useState(null);
  const load = useCallback(() => api.get(`/payments/orders/${id}`).then((r) => setO(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!o || !["created", "pending"].includes(o.status)) return;
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
  }, [o, load]);
  if (!o) return null;
  const ok = o.status === "paid";
  const Icon = ok ? CheckCircle2 : ["failed", "cancelled"].includes(o.status) ? XCircle : Clock3;
  const color = ok ? "text-emerald-600" : ["failed", "cancelled"].includes(o.status) ? "text-red-400" : "text-amber-600";
  return (
    <div className="mx-auto max-w-lg" data-testid="receipt-page">
      <Card className="text-center">
        <Icon size={52} className={`mx-auto ${color}`} />
        <h1 className="mt-3 text-2xl font-bold text-white" data-testid="receipt-status-title">
          {ok ? t("Payment confirmed", "भुगतान सफल") : o.status === "refunded" ? t("Payment refunded", "भुगतान रिफंड") : ["failed", "cancelled"].includes(o.status) ? t("Payment not completed", "भुगतान पूरा नहीं हुआ") : t("Awaiting confirmation", "पुष्टि की प्रतीक्षा")}
        </h1>
        <div className="mt-2"><StatusBadge status={o.status} testid="receipt-status" /></div>
        {!ok && ["created", "pending"].includes(o.status) && <p className="mt-2 text-sm text-slate-500">{t("We activate your plan only after the payment provider confirms. This page refreshes automatically.", "प्रोवाइडर की पुष्टि के बाद ही प्लान सक्रिय होगा। यह पेज अपने आप रिफ्रेश होता है।")}</p>}
        <div className="mt-6 space-y-2 rounded-2xl bg-white/5 p-4 text-left text-sm">
          {[["Plan", o.plan_name], [t("Type", "प्रकार"), o.kind], [t("Transaction ID", "लेनदेन ID"), o.id], [t("Provider order", "प्रोवाइडर ऑर्डर"), o.provider_order_id], [t("Provider payment", "प्रोवाइडर भुगतान"), o.provider_payment_id || "—"], [t("Receipt no.", "रसीद संख्या"), o.receipt_no || "—"], [t("Base", "मूल"), inr(o.base)], [`GST ${o.rate}%`, inr(o.tax)], [t("Total", "कुल"), inr(o.total)], [t("Date", "तारीख"), dt(o.paid_at || o.created_at)], ["Mode", `${o.mode?.toUpperCase()} • ${o.provider}`]].map(([l, v]) => (
            <div key={l} className="flex justify-between gap-4"><span className="text-slate-500">{l}</span><span className="break-all text-right font-medium text-white" data-testid={`receipt-${String(l).toLowerCase().replace(/[^a-z]/g, "-")}`}>{v}</span></div>
          ))}
        </div>
        {o.invoice && <p className="mt-3 text-xs text-slate-500" data-testid="receipt-doc-type">{o.invoice.type === "tax_invoice" ? "Tax invoice" : t("Payment receipt — not a GST tax invoice", "भुगतान रसीद — GST टैक्स इनवॉइस नहीं")}</p>}
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          {["failed", "cancelled"].includes(o.status) && <Link to={`/app/checkout/${o.plan_id}`} className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-blue-500 px-5 py-2.5 text-sm font-bold text-white" data-testid="retry-payment-btn"><RotateCcw size={16} />{t("Retry payment", "फिर से भुगतान करें")}</Link>}
          <Link to="/app/payments" className="rounded-full border border-white/10 px-5 py-2.5 text-sm font-semibold" data-testid="receipt-history-btn">{t("View transaction history", "लेनदेन इतिहास देखें")}</Link>
          {ok && <Link to="/app/surveys" className="rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 px-5 py-2.5 text-sm font-bold text-white" data-testid="receipt-surveys-btn">{t("Go to surveys", "सर्वे पर जाएँ")}</Link>}
        </div>
      </Card>
    </div>
  );
}

export function Payments() {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/payments/orders").then((r) => setRows(r.data)); }, []);
  return (
    <div data-testid="payments-history">
      <PageTitle title={t("Transaction history", "लेनदेन इतिहास")} sub={t("Plan activation and upgrade payments", "प्लान एक्टिवेशन और अपग्रेड भुगतान")} />
      <Table rows={rows} empty={t("No payments yet", "अभी कोई भुगतान नहीं")} cols={[
        { k: "created_at", l: t("Date", "तारीख"), r: (r) => dt(r.created_at) }, { k: "plan_name", l: "Plan" }, { k: "kind", l: t("Type", "प्रकार") },
        { k: "total", l: t("Total", "कुल"), r: (r) => inr(r.total) }, { k: "status", l: "Status", r: (r) => <StatusBadge status={r.status} /> },
        { k: "receipt_no", l: t("Receipt", "रसीद"), r: (r) => r.receipt_no || "—" },
        { k: "a", l: "", r: (r) => <Link to={`/app/payments/${r.id}`} className="text-sm font-semibold text-blue-400" data-testid={`view-order-${r.id}`}>{t("View", "देखें")}</Link> },
      ]} />
    </div>
  );
}
